import { memo } from "react";
import { UseFormHandleSubmit, UseFormRegister, FieldErrors } from "react-hook-form";
import { LoginErrorMessage } from "./LoginErrorMessage";
import { LoginFormFields } from "./LoginFormFields";
import { LoginFormActions } from "./LoginFormActions";
import { useTranslation } from "@/hooks/useTranslation";

type LoginFormData = {
  user_email: string;
  user_password: string;
};

interface LoginFormProps {
  currentLocale: string;
  register: UseFormRegister<LoginFormData>;
  handleSubmit: UseFormHandleSubmit<LoginFormData>;
  errors: FieldErrors<LoginFormData>;
  isSubmitting: boolean;
  loading: boolean;
  loginError: string | null;
  unverifiedEmail?: string | null;
  resendingVerification?: boolean;
  onResendVerification?: () => Promise<void>;
  onSubmit: (data: LoginFormData) => Promise<void>;
  onFocus: () => void;
}

export const LoginForm = memo(function LoginForm({
  currentLocale,
  register,
  handleSubmit,
  errors,
  isSubmitting,
  loading,
  loginError,
  unverifiedEmail,
  resendingVerification,
  onResendVerification,
  onSubmit,
  onFocus,
}: LoginFormProps) {
  const { t } = useTranslation();

  return (
    <form 
      className="space-y-5" 
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        // Prevent any default form behavior
        handleSubmit(onSubmit)(e).catch(() => {
          // Silently handle errors - they're already handled in onSubmit
        });
      }}
      noValidate
    >
      {/* Error Message */}
      {loginError && (
        <LoginErrorMessage 
          error={loginError} 
          checkCredentialsText={t('auth.checkCredentials')}
        />
      )}

      {unverifiedEmail && (
        <div
          data-testid="unconfirmed-account-warning"
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100"
        >
          <p className="mb-2">{t('auth.emailVerification.unconfirmedWarning')}</p>
          {onResendVerification && (
            <button
              type="button"
              disabled={resendingVerification}
              onClick={onResendVerification}
              data-testid="login-resend-verification-button"
              className="font-semibold text-pink-600 underline hover:text-pink-700 disabled:opacity-50 dark:text-pink-400"
            >
              {t('auth.emailVerification.resendEmail')}
            </button>
          )}
        </div>
      )}
      
      <LoginFormFields 
        register={register}
        errors={errors}
        onFocus={onFocus}
      />

      <LoginFormActions 
        currentLocale={currentLocale}
        isSubmitting={isSubmitting}
        loading={loading}
      />
    </form>
  );
});

